import { ID, UserSettings } from '@griply/types';

import { dateToFirestoreTimestamp } from '../converters/date-to-firestore-timestamp';
import { FirestoreInstance } from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';

/**
 * Type guard to check if a value is a plain object (not array, not null, not Date).
 */
const isRecord = (val: unknown): val is Record<string, unknown> =>
  val !== null &&
  typeof val === 'object' &&
  !Array.isArray(val) &&
  !(val instanceof Date);

/**
 * Flattens a nested object into dot-notation keys for Firestore partial updates.
 * e.g. { allTasks: { collapsedLifeAreas: ['a'] } }
 * becomes { 'allTasks.collapsedLifeAreas': ['a'] }
 */
export const flattenSettings = (
  obj: Record<string, unknown>,
  prefix = '',
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const key of Object.keys(obj)) {
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (isRecord(obj[key])) {
      Object.assign(
        result,
        flattenSettings(obj[key] as Record<string, unknown>, newKey),
      );
      continue;
    }

    if (obj[key] === undefined) {
      continue;
    }

    result[newKey] = obj[key];
  }

  return result;
};

export type UpdateUserSettingsOptions<
  TDocRef = unknown,
  TFieldValue = unknown,
  TTimestamp = unknown,
  TDb = FirestoreInstance,
> = {
  userSettings: Partial<UserSettings>;
  userId: ID;
  doc: (db: NoInfer<TDb>, path: string, ...rest: string[]) => TDocRef;
  updateDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<string, unknown>,
  ) => Promise<void>;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  timestampFromDate: (date: Date) => TTimestamp;
};

export const updateUserSettings = async <
  TDocRef,
  TFieldValue,
  TTimestamp,
  TDb = FirestoreInstance,
>(
  options: UpdateUserSettingsOptions<TDocRef, TFieldValue, TTimestamp, TDb>,
): Promise<void> => {
  const {
    userSettings,
    userId,
    doc,
    updateDoc,
    db,
    serverTimestamp,
    timestampFromDate,
  } = options;

  const userRef = doc(db, FirestoreCollections.Users, userId);

  const flattenedSettings = flattenSettings(
    userSettings as Record<string, unknown>,
    'settings',
  );
  const convertedSettings = dateToFirestoreTimestamp(
    flattenedSettings,
    timestampFromDate,
  );

  updateDoc(userRef, {
    ...(convertedSettings as Record<string, unknown>),
    updatedAt: serverTimestamp(),
  });
};
